/**
 * Client-side entry point — boots the React app in the browser.
 *
 * Hydrates the server-rendered HTML produced by entry-server.tsx (via the
 * prerender script) instead of doing a full client-side render. This makes
 * the page interactive without re-rendering the DOM that's already there
 * from the static HTML.
 *
 * When prerender bodies are missing (e.g. during local dev without a built
 * dist or for a route that wasn't pre-rendered), React falls back to a
 * normal first render — hydrateRoot handles both cases gracefully.
 */

import { StrictMode } from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AppRoutes, LazyToaster } from "./App";
import "./index.css";
import "./i18n";
import { scheduleAnalytics } from "./lib/analytics";

const rootEl = document.getElementById("root")!;

const tree = (
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <LazyToaster />
        <AppRoutes />
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>
);

// If the SSR pipeline pre-rendered the body, hydrate it. Otherwise (empty
// root, e.g. in dev mode or for unrendered routes), do a fresh client render.
// We detect by checking for our SSR marker comment inside #root.
// The dev-time index.html contains the bare `<!--ssr-body-->` placeholder with
// no markup around it, so the marker alone is not enough — we also require at
// least one real element to have been rendered into #root.
const hasSSRMarkup =
  rootEl.childElementCount > 0 &&
  Array.from(rootEl.childNodes).some(
    (n) => n.nodeType === Node.COMMENT_NODE && n.textContent?.includes("ssr-body")
  );

if (hasSSRMarkup) {
  hydrateRoot(rootEl, tree);
} else {
  createRoot(rootEl).render(tree);
}

// Kick off PostHog after page load (deferred, idle-friendly).
scheduleAnalytics();
